
import { motion, useTransform, type MotionValue } from "framer-motion";
import { FLOATING_ICONS, DRIFT_NODE_ID, SNAP_FADE } from "../keyframe-data";
import type { KeyframeBand } from "../types";
import {
  LAYOUT,
  EDGE,
  NODE_CARD,
  polylinePath,
  type LayoutNodeV2,
} from "../graph-layout";
import { TierLanes } from "../elements/tier-lanes";
import { NodeCard, type ServiceIconId } from "../elements/node-card";
import type { FloatingIcon } from "../types";

const FLOATING_BY_ID = new Map<string, FloatingIcon>(
  FLOATING_ICONS.map((f) => [f.id, f]),
);

function isDriftEdge(fromId: string, toId: string): boolean {
  return fromId === "lambda" && toId === DRIFT_NODE_ID;
}

interface SettlingNodeProps {
  progress: MotionValue<number>;
  band: KeyframeBand;
  icon: FloatingIcon;
  node: LayoutNodeV2;
}

function SettlingNode({ progress, band, icon, node }: SettlingNodeProps) {
  const startX = icon.x - NODE_CARD.width / 2;
  const startY = icon.y - NODE_CARD.height / 2;

  // Settle in the first half of beat 3, then hold the settled position for
  // the remainder of the band. The hold gives the user a moment to recognize
  // "the graph is now in place" before beat 4 begins.
  const settleEnd = band.start + (band.end - band.start) * 0.5;
  const x = useTransform(progress, [band.start, settleEnd], [startX, node.x]);
  const y = useTransform(progress, [band.start, settleEnd], [startY, node.y]);

  return (
    <motion.g style={{ x, y }}>
      <NodeCard id={node.id as ServiceIconId} x={0} y={0} w={node.w} />
    </motion.g>
  );
}

interface Props {
  progress: MotionValue<number>;
  band: KeyframeBand;
}

export function Beat3Graph({ progress, band }: Props) {
  const outerOpacity = useTransform(
    progress,
    [band.start, band.start + SNAP_FADE],
    [0, 1],
  );

  const halfEnd = band.start + (band.end - band.start) / 2;
  const edgeOpacity = useTransform(progress, [band.start, halfEnd], [0, 1]);

  return (
    <motion.g style={{ opacity: outerOpacity }} data-beat="3" aria-hidden="true">
      {/* Tier dividers + headers — AI/ML hidden at beat 3. */}
      <motion.g style={{ opacity: edgeOpacity }}>
        <TierLanes showAiml={false} />
      </motion.g>

      {/* Edges — every edge dashed (uniform deferred dash); drift edge to bedrock excluded (renders in beat 5). */}
      <motion.g style={{ opacity: edgeOpacity }}>
        {LAYOUT.edges.filter((e) => !isDriftEdge(e.from, e.to)).map((e) => (
          <path
            key={e.id}
            d={polylinePath(e.points)}
            fill="none"
            stroke={EDGE.flow}
            strokeWidth={EDGE.flowWidth}
            strokeDasharray={EDGE.copyDash}
            strokeLinejoin="round"
            strokeLinecap="round"
            markerEnd="url(#arrow-flow)"
            className="edge-march"
          />
        ))}
      </motion.g>

      {/* Settling node cards — Bedrock excluded; appears beat 5+. */}
      {LAYOUT.nodes.filter((n) => n.id !== DRIFT_NODE_ID).map((node) => {
        const icon = FLOATING_BY_ID.get(node.id);
        if (!icon) return null;
        return (
          <SettlingNode
            key={node.id}
            progress={progress}
            band={band}
            icon={icon}
            node={node}
          />
        );
      })}
    </motion.g>
  );
}
